'use client';
import { useSearch } from '@hooks/search/useSearch';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import SearchListItem from './_SearchListItem';

interface Props {
  searchTerm: string;
  searchType: string;
  page?: string;
  resultTextSingular: string;
  resultTextPlural: string;
}

const SearchResultsList = ({
  searchTerm,
  searchType,
  page,
  resultTextSingular,
  resultTextPlural,
}: Props) => {
  const size = parseInt(page ?? '1');
  const {
    data: state,
    isLoading,
    setSize,
  } = useSearch({ searchTerm, searchType, initialSize: size });
  const { path } = useCurrentUrl();
  const { t } = useTranslation();
  React.useEffect(() => {
    setSize(size);
  }, [page]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-mt-5 tw-mb-3" data-testid="search-in-progress">
        <div className="tw-w-12 tw-h-12 tw-border-4 tw-border-brand-300 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="tw-mx-auto tw-my-6 tw-max-w-[900px]">
      <p className="tw-font-bold tw-text-body-1">{`${state.nbHits} ${
        state.nbHits === 1 ? resultTextSingular || 'Result' : resultTextPlural || 'Results'
      }`}</p>

      <div className="tw-pt-3">
        <ul id="results" className="tw-list-none tw-pl-0" role="listbox" tabIndex={0}>
          {state.results.map((item: any, i: number) => (
            <SearchListItem key={`${searchTerm}_${i}`} {...item} searchType={searchType} />
          ))}
        </ul>
        {size < state.nbPages && (
          <div className="tw-flex tw-justify-center tw-mt-4">
            <Button
              variant={ButtonVariant.tertiary}
              as={Anchor}
              link={`${path}?page=${state.page}`}
              onClick={(e) => {
                e.preventDefault();
                const url = new URL(window.location.href);
                url.searchParams.set('page', String(size + 1));
                window.history.pushState(null, '', url);
              }}
            >
              {t('gridContent.viewMore', undefined, { default: 'View More' })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsList;
